import { useTalkToSalesUpsellsVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useTalkToSalesUpsellsVariation.js';
import { useBlockUpsellForEnterpriseConsent } from '#pplx/frontend/apps/ask/organization/hooks/useBlockUpsellForEnterpriseConsent.js';
import { useCanManageOrgSettingsState } from '#pplx/frontend/apps/ask/organization/hooks/useCanManageOrgSettings.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { useTalkToSalesTargeting } from '#pplx/frontend/apps/ask/talk-to-sales/useTalkToSalesTargeting.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useUserInfo } from '#pplx/frontend/user-info/useUserInfo.js';
const ELIGIBILITY_REASON = 'talk-to-sales-eligibility';
const INELIGIBLE = { isLoading: false, eligible: false, audience: null };
const LOADING = { isLoading: true, eligible: false, audience: null };
/**
 * Whether the current user should see a talk-to-sales CTA, and as which
 * audience. Every branch fails closed while loading so surfaces never flash.
 *
 * The `talk-to-sales-upsells` Eppo flag is the killswitch: it is keyed on
 * user_nextauth_id, so logged-out users resolve to the default (off) and all
 * data fetches below stay disabled until the flag is on.
 */
export function useTalkToSalesEligibility() {
    const { variation: isFlagEnabled, loading: isFlagLoading } = useTalkToSalesUpsellsVariation({
        defaultValue: false,
    });
    const { session, status: sessionStatus } = useSession();
    const targeting = useTalkToSalesTargeting({
        enabled: isFlagEnabled && sessionStatus === 'authenticated',
        userId: session?.user?.id ?? null,
    });
    const isEnterpriseUser = useIsEnterpriseUser();
    const isBlockedForEnterpriseConsent = useBlockUpsellForEnterpriseConsent({
        enabled: isFlagEnabled,
    });
    const { organization, isLoading: isOrgDataLoading } = useUserOrgData({
        enabled: isFlagEnabled,
        reason: ELIGIBILITY_REASON,
    });
    // Only self-serve, consent-enabled orgs can be eligible, so gate the
    // effective-permissions fetch on those (already-fetched) org attributes —
    // otherwise every member of every sales-assisted org would fire an RBAC
    // request whose result is discarded by the checks below.
    const { canManageOrgSettings, isLoading: isPermissionsLoading } = useCanManageOrgSettingsState({
        enabled: isFlagEnabled &&
            isEnterpriseUser &&
            !isBlockedForEnterpriseConsent &&
            organization?.org_service_type === 'SELF_SERVE' &&
            organization?.subscription_source !== 'salesforce_netsuite',
    });
    const { isGov, query: userInfoQuery } = useUserInfo({
        enabled: isFlagEnabled,
        reason: ELIGIBILITY_REASON,
    });
    if (isFlagLoading) {
        return LOADING;
    }
    if (!isFlagEnabled) {
        return INELIGIBLE;
    }
    // A loading session reads as non-enterprise, which would briefly classify an
    // org admin as the consumer audience (and emit consumer analytics). Hold
    // until the session settles.
    if (sessionStatus === 'loading') {
        return LOADING;
    }
    if (sessionStatus !== 'authenticated') {
        return INELIGIBLE;
    }
    if (targeting.isLoading) {
        return LOADING;
    }
    if (targeting.isError || !targeting.targeted) {
        return INELIGIBLE;
    }
    if (userInfoQuery.isError) {
        return INELIGIBLE;
    }
    // useUserInfo serves placeholder data while fetching, so isLoading stays
    // false; isPlaceholderData is the real "not yet resolved" signal here.
    if (userInfoQuery.isPlaceholderData) {
        return LOADING;
    }
    if (isGov) {
        return INELIGIBLE;
    }
    if (isEnterpriseUser) {
        if (isOrgDataLoading || isPermissionsLoading) {
            return LOADING;
        }
        // Enterprise consent: shares useUserOrgData's query with the fetch above,
        // so by this point (org data loaded) the answer is settled.
        if (isBlockedForEnterpriseConsent) {
            return INELIGIBLE;
        }
        // Sales-assisted (white-glove) and SBKK orgs already have an account
        // manager; only self-serve orgs are upsell targets.
        if (organization?.org_service_type !== 'SELF_SERVE') {
            return INELIGIBLE;
        }
        if (organization?.subscription_source === 'salesforce_netsuite') {
            return INELIGIBLE;
        }
        if (!canManageOrgSettings) {
            return INELIGIBLE;
        }
        return { isLoading: false, eligible: true, audience: 'selfServeOrgAdmin' };
    }
    return { isLoading: false, eligible: true, audience: 'consumer' };
}
//# sourceMappingURL=useTalkToSalesEligibility.js.map